import { Button, message } from "antd";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../Contexts/AuthContext";
import { CartContext } from "../../Contexts/CartContext";
import { AuthModalContext } from "../../Contexts/AuthModalContext";
import { ConfigContext } from "../../Contexts/ConfigContext";
import axios from "../../config/axios";
import {
  getVariantImage,
  getVariantPrice,
  getWeightInfo,
  sortVariantsByPrice,
} from "../../utils/helpers";
import styles from "./ProductCard.module.scss";
import { ProductPrice } from "./subcomponents/ProductCardPrice/ProductCardPrice";
import { ProductCounter } from "./subcomponents/ProductCounter/ProductCounter";
import { ProductCardImagePanel } from "./subcomponents/ProductCardImagePanel/ProductCardImagePanel";
import { ProductCardVariants } from "./subcomponents/ProductCardVariants/ProductCardVariants";

const ProductCard = ({ product = {}, discountOnly }) => {
  const navigate = useNavigate();
  const cartContext = useContext(CartContext);
  const { user, isAuthenticated, location } = useContext(AuthContext);
  const { setShowLocModal } = useContext(ConfigContext);
  const { openSignIn } = useContext(AuthModalContext);

  const variants = sortVariantsByPrice(
    discountOnly
      ? product.variants.filter((variant) => !!variant.discount_percentage)
      : product.variants,
  );

  const [selectedVariant, setSelectedVariant] = useState(variants?.[0] ?? {});
  const [finalPrice, setFinalPrice] = useState(0.0);
  const [quantity, setQuantity] = useState(1);
  const [productInWishlist, setProductInWishlist] = useState(false);
  const [productInCart, setProductInCart] = useState(false);
  const [requestStockPopOpen, setRequestStockPopOpen] = useState(false);

  useEffect(() => {
    if (selectedVariant) {
      setFinalPrice(getVariantPrice(selectedVariant));
    }
  }, [selectedVariant]);

  useEffect(() => {
    setProductInCart(false);
    if (cartContext.cartItems) {
      for (let i = 0; i < cartContext.cartItems.length; i++) {
        if (cartContext.cartItems[i].id === product.id) {
          setProductInCart(true);
          setQuantity(cartContext.cartItems[i].quantity);
          break;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...Object.values(cartContext)]);

  //defining increaseQty function
  const increaseQty = () => {
    if (!productInCart) {
      setQuantity(quantity + 1);
    } else {
      addToCart();
    }
    // TODO:max stock check
  };

  const decreaseQty = () => {
    if (quantity - 1 === 0) {
      cartContext.removeFromCart(product.id);
    }
    if (quantity - 1 < 1) {
      setQuantity(1);
    } else {
      cartContext.cartItemDecrement(product.id);
      setQuantity(quantity - 1);
    }
  };

  const addToCart = () => {
    //checking if user added location
    if (!location) {
      setShowLocModal(true);
      return;
    }

    let item = {
      id: product.id,
      title: product.title,
      slug: product.slug,
      order_time_price: parseFloat(finalPrice).toFixed(2),
      image: getVariantImage(selectedVariant, product.variants),
      tax_rate: product.tax_rate,
      quantity: quantity,
      stock: selectedVariant.stock,
      product: product,
      variant: selectedVariant,
    };

    cartContext.addToCart(item);
  };

  useEffect(() => {
    if (user?.wishlist?.includes(product.id)) {
      setProductInWishlist(true);
    }
  }, [product, user]);

  const addToWishlist = () => {
    //TODO: Auth check
    if (!isAuthenticated) {
      openSignIn();
      return;
    }

    setProductInWishlist(true);

    let wishlist = user.wishlist ?? [];
    wishlist.push(product.id);
    let payload = {
      wishlist: wishlist,
    };
    axios
      .patch(`/users/update`, payload, { withCredentials: true })
      .then((response) => {});
  };

  const removeFromWishlist = () => {
    setProductInWishlist(false);

    let wishlist = user.wishlist;
    let index = user.wishlist.indexOf(product.id);
    wishlist.splice(index, 1);

    let payload = {
      wishlist: wishlist,
    };
    axios
      .patch(`/users/update`, payload, { withCredentials: true })
      .then((response) => {});
  };

  // request for stock
  const requestStock = async (product) => {
    setRequestStockPopOpen(false);
    if (!isAuthenticated) {
      openSignIn();
      return;
    }
    try {
      message.loading("Requesting stock...");
      const payload = {
        name: `${user.first_name} ${user.last_name ? user.last_name : ""}`,
        email: user.email ? user.email : "anonymous@dfresh.co",
        subject: "Request for stock",
        message: `Please add stock for ${product.title}, that is currently out of stock. The product link is: https://www.desifresh.co/products/${product.slug}`,
      };
      await axios.post(`/contact-us/submit`, payload, {
        withCredentials: true,
      });
      message.destroy();
      message.success(
        "Request sent! We'll notify you when the product is back in stock.",
      );
    } catch (error) {
      message.error("Something went wrong!");
    }
  };

  // Product Card Components

  const ProductName = () => (
    <Link to={`/products/${product.slug}`}>
      <div className={styles.productCard_name}>
        <div title={product.title} className={styles.productCard_name_text}>
          {product.title}
        </div>
      </div>
    </Link>
  );

  const ProductWeightPrize = () => {
    const weightInfo = getWeightInfo(selectedVariant, finalPrice);
    if (!weightInfo) return null;
    return (
      <div className={styles.weight_info}>
        <span style={{ fontSize: "1.4rem" }}>{weightInfo}</span>
      </div>
    );
  };

  const ProductBrand = () => (
    <>
      {product.brand && (
        <div
          className={styles.productCard_brand}
          onClick={() => {
            navigate(`/brand/${product.brand.slug}_${product.brand.id}`);
          }}
        >
          <span
            style={{
              textDecoration: "underline",
            }}
          >
            {product.brand.name}
          </span>
        </div>
      )}
    </>
  );

  const ProductAddToCartNew = () => {
    if (productInCart) {
      return (
        <ProductCounter
          quantity={quantity}
          increaseQty={increaseQty}
          decreaseQty={decreaseQty}
        />
      );
    }
    if (selectedVariant.stock > 0) {
      return (
        <Button type="primary" onClick={addToCart} size="medium">
          Add to Cart
          <img
            src="/icon/cart.svg"
            style={{ width: "24px", height: "24px" }}
            alt="cart"
          />
        </Button>
      );
    }
    return null;
  };

  return (
    <>
      <div className={styles.productCard}>
        <ProductCardImagePanel
          product={product}
          selectedVariant={selectedVariant}
          productInWishlist={productInWishlist}
          addToWishlist={addToWishlist}
          removeFromWishlist={removeFromWishlist}
        />
        <div className={styles.productCard_details}>
          <ProductBrand />
          <ProductName />
          <ProductPrice
            selectedVariant={selectedVariant}
            finalPrice={finalPrice}
          />
          <div style={{ display: 'grid', gap: '1rem' }}>
            <ProductCardVariants
              variants={variants}
              selectedVariant={selectedVariant}
              setSelectedVariant={setSelectedVariant}
            />
            <ProductAddToCartNew />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductCard;
